import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Dcr,
	DcrEntityType,
	DcrFieldResult,
	DcrType,
	Hcphco,
	ParentHco
} from '@/core/graphql/generated/types';
import { PRTN_RELATIONSHIP_TYPE_ID } from '@/pages/HcoProfilePage/sections/HcoAffiliationsSection/_constants/hcoAffiliationSections';

import { processEntityData } from '../../_utils/processEntityData';
import { useGetRelationshipTypeOptionsQuery } from '../_graphql/query/GetRelationshipTypeOptions.hook';

import { useHcoFieldNames, HcoSections } from './useHcoFieldNames';
import { useHcpFieldNames } from './useHcpFieldNames';

const RELATIONSHIP_TYPE_FIELDS = ['typeOfRelationship_V', 'relationshipType_V'];

const isPartnerAffiliation = (
	group: string,
	vid: string,
	dcr: Dcr,
	profile: Hcphco | null,
	partnerRelationshipTypeName: string | undefined
) => {
	const relationshipTypeChange = dcr.fieldResults?.find(
		field =>
			field.group === group && RELATIONSHIP_TYPE_FIELDS.includes(field.field)
	);

	if (relationshipTypeChange) {
		const values = [
			relationshipTypeChange.requestedValue,
			relationshipTypeChange.finalValue,
			relationshipTypeChange.previousValue
		];

		return (
			values.includes(PRTN_RELATIONSHIP_TYPE_ID) ||
			(!!partnerRelationshipTypeName &&
				values.includes(partnerRelationshipTypeName))
		);
	}

	if (vid === 'new' || !profile) return false;
	const affiliation =
		getAffiliations(profile)[getAffiliationIndex(vid, profile)];

	return affiliation?.relationshipType?.code === PRTN_RELATIONSHIP_TYPE_ID;
};

export type ResultWithType = DcrFieldResult & { type: DcrEntityType };

export type TableRowType = ResultWithType | { label: string };

const getProfileCountryId = (profile: Hcphco | null): string | undefined => {
	if (!profile) return undefined;

	const rawCountry =
		profile.countryId ??
		(profile as unknown as Record<string, unknown>).primaryCountry_V;

	return typeof rawCountry === 'string' ? rawCountry.toLowerCase() : undefined;
};

export const useDcr = (dcr: Dcr | null) => {
	const { t } = useTranslation();
	const { hcpSectionMap, hcpSections } = useHcpFieldNames();
	const { hcoSectionMap, hcoSections } = useHcoFieldNames();

	const { data: relationshipTypeData } = useGetRelationshipTypeOptionsQuery({
		skip: !dcr || dcr.entityType !== DcrEntityType.Hco
	});

	const partnerRelationshipTypeName = useMemo(() => {
		const result = relationshipTypeData?.RELATIONSHIP_TYPE;
		if (result?.__typename !== 'ReferenceResult') return undefined;

		return result.results.find(
			option => option.id === PRTN_RELATIONSHIP_TYPE_ID
		)?.name;
	}, [relationshipTypeData]);

	const parentHcosGroupLabels = useMemo(() => {
		if (!dcr || dcr.entityType !== DcrEntityType.Hco) return {};

		const currentEntity = dcr.currentEntity ?? null;
		const affiliations = getAffiliations(currentEntity);
		const groups: string[] = [];
		const seenGroups = new Set<string>();

		dcr.fieldResults?.forEach(field => {
			if (
				field.group.startsWith('parentHcos_V:') &&
				!seenGroups.has(field.group)
			) {
				seenGroups.add(field.group);
				groups.push(field.group);
			}
		});

		const entries = groups.map(group => {
			const [, vid, newIndexRaw] = group.split(':');

			return {
				group,
				vid,
				newIndex: newIndexRaw !== undefined ? Number(newIndexRaw) : -1,
				isPartner: isPartnerAffiliation(
					group,
					vid,
					dcr,
					currentEntity,
					partnerRelationshipTypeName
				)
			};
		});

		const existingOrder = (vid: string) => {
			const index = affiliations.findIndex(
				affiliation => affiliation.vid_V === vid
			);

			return index === -1 ? Infinity : index;
		};

		const sortedEntries = [...entries].sort((a, b) => {
			const aIsNew = a.vid === 'new';
			const bIsNew = b.vid === 'new';

			if (aIsNew !== bIsNew) return aIsNew ? 1 : -1;

			return aIsNew
				? a.newIndex - b.newIndex
				: existingOrder(a.vid) - existingOrder(b.vid);
		});

		const counters = { partner: 0, other: 0 };
		const labels: Record<string, string> = {};

		sortedEntries.forEach(entry => {
			const counterKey = entry.isPartner ? 'partner' : 'other';
			counters[counterKey] += 1;

			const labelText = entry.isPartner
				? hcoSections[HcoSections.Partners]
				: t('Affiliation');
			labels[entry.group] = `${labelText} ${counters[counterKey]}`;
		});

		return labels;
	}, [dcr, partnerRelationshipTypeName, hcoSections, t]);

	const determineGroup = useCallback(
		(group: string, field: string, dcr: Dcr) => {
			const values = group.split(':');
			const vid = values[1];
			const newIndex = values[2] !== undefined ? Number(values[2]) + 1 : 0;
			const profile = dcr.currentEntity ?? null;

			if (group === 'root') {
				if (dcr.entityType === DcrEntityType.Hcp) {
					return (
						hcpSections[hcpSectionMap[field as keyof typeof hcpSectionMap]] ||
						t('Other')
					);
				}
				return (
					hcoSections[hcoSectionMap[field as keyof typeof hcoSectionMap]] ||
					t('Other')
				);
			}

			if (group.startsWith('addresses_V:')) {
				if (vid === 'new') {
					return `${t('Address')} ${(profile?.addresses.length ?? 0) + newIndex}`;
				}
				return `${t('Address')} ${profile ? getAddressIndex(vid, profile) + 1 : ''}`;
			} else if (group.startsWith('licenses_V:')) {
				if (vid === 'new') {
					return `${t('License')} ${(profile?.licenses.length ?? 0) + newIndex}`;
				}
				return `${t('License')} ${profile ? getLicenseIndex(vid, profile) + 1 : ''}`;
			} else if (group.startsWith('parentHcos_V:')) {
				if (dcr.entityType === DcrEntityType.Hco) {
					return parentHcosGroupLabels[group] ?? t('Affiliation');
				}

				if (vid === 'new') {
					return `${t('Affiliation')} ${(getAffiliations(profile).length ?? 0) + newIndex}`;
				}
				return `${t('Affiliation')} ${profile ? getAffiliationIndex(vid, profile) + 1 : ''}`;
			}

			return t('Other');
		},
		[
			t,
			hcoSections,
			hcoSectionMap,
			hcpSections,
			hcpSectionMap,
			parentHcosGroupLabels
		]
	);

	const profile = useMemo(
		() =>
			dcr?.type === DcrType.Change
				? dcr?.currentEntity || null
				: dcr?.entityData
					? processEntityData(dcr.entityData, dcr.entityType)
					: null,
		[dcr]
	);

	const countryId = useMemo(() => getProfileCountryId(profile), [profile]);

	const processGbtoUk = (field: DcrFieldResult) => {
		if (field.field !== 'countryCda_V') return field;

		return {
			...field,
			requestedValue:
				field.requestedValue === 'Great Britain'
					? 'United Kingdom'
					: field.requestedValue,
			previousValue:
				field.previousValue === 'Great Britain'
					? 'United Kingdom'
					: field.previousValue,
			finalValue:
				field.finalValue === 'Great Britain'
					? 'United Kingdom'
					: field.finalValue
		};
	};

	const formatYesNo = useCallback(
		(value: string | null | undefined) => {
			if (value == null) return value;
			if (value.trim().toUpperCase() === 'Y') return t('Yes');
			if (value.trim().toUpperCase() === 'N') return t('No');
			return value;
		},
		[t]
	);

	const processBooleanFields = useCallback(
		(field: DcrFieldResult) => {
			if (field.field !== 'prescriberCda_V') return field;

			return {
				...field,
				requestedValue: formatYesNo(field.requestedValue),
				previousValue: formatYesNo(field.previousValue),
				finalValue: formatYesNo(field.finalValue)
			};
		},
		[formatYesNo]
	);

	const data: TableRowType[] = useMemo(() => {
		if (!dcr?.fieldResults) return [];

		const grouped = dcr.fieldResults.reduce(
			(acc, item) => {
				const groupName = determineGroup(item.group, item.field, dcr);
				if (!acc[groupName]) acc[groupName] = [];
				acc[groupName].push({
					type: dcr.entityType,
					...processBooleanFields(processGbtoUk(item))
				});
				return acc;
			},
			{} as Record<string, ResultWithType[]>
		);

		const groupOrder = Object.values(
			dcr.entityType === DcrEntityType.Hcp ? hcpSections : hcoSections
		).reduce(
			(acc, item, i) => {
				acc[item] = i;
				return acc;
			},
			{} as Record<string, number>
		);

		const fieldOrder = Object.keys(
			dcr.entityType === DcrEntityType.Hcp ? hcpSectionMap : hcoSectionMap
		).reduce(
			(acc, item, i) => {
				acc[item] = i;
				return acc;
			},
			{} as Record<string, number>
		);

		return Object.entries(grouped)
			.sort(([a], [b]) => {
				const indexA = groupOrder[a.split(' ')[0]] ?? Infinity;
				const indexB = groupOrder[b.split(' ')[0]] ?? Infinity;

				if (indexA === indexB) {
					return a.localeCompare(b, undefined, { numeric: true });
				}

				return indexA - indexB;
			})
			.flatMap(([groupName, items]) => {
				const sortedItems = [...items].sort(
					(a, b) =>
						(fieldOrder[a.field] ?? Infinity) -
						(fieldOrder[b.field] ?? Infinity)
				);

				return [{ label: groupName }, ...sortedItems];
			});
	}, [
		dcr,
		determineGroup,
		hcpSections,
		hcpSectionMap,
		hcoSections,
		hcoSectionMap,
		processBooleanFields
	]);

	return {
		data,
		profile,
		countryId
	};
};

const getAddressIndex = (addressVid: string, profile: Hcphco) => {
	return profile.addresses?.findIndex(address => address.vid_V === addressVid);
};

const getLicenseIndex = (licenseVid: string, profile: Hcphco) => {
	return profile.licenses?.findIndex(license => license.vid_V === licenseVid);
};

const getAffiliations = (profile: Hcphco | null | undefined) => {
	if (!profile) return [];
	const search = profile.affiliationSearch;
	return (
		search?.__typename === 'HCPAffiliationsResults' ||
		search?.__typename === 'HCOAffiliationsResults'
			? (search.results ?? [])
			: []
	) as ParentHco[];
};

const getAffiliationIndex = (affiliationVid: string, profile: Hcphco) => {
	return getAffiliations(profile).findIndex(
		hco => hco.vid_V === affiliationVid
	);
};
